import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { Toaster } from "react-hot-toast";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import ViewCampaignPage from "./app/administration/campaign-management/[id]/page";
import CreateCampaignPage from "./app/administration/campaign-management/create/page";
import CampaignManagementPage from "./app/administration/campaign-management/page";
import TpmoPage from "./app/campaigns/tpmo/page";
import HomePage from "./app/home/page";
import NotFoundPage from "./app/not-found/page";
import PrescreeningDetailsPage from "./app/campaigns/candidate-outreach/details/[id]/page";
import PrescreeningEditPage from "./app/campaigns/candidate-outreach/edit/[id]/page";
import PrescreeningPage from "./app/campaigns/candidate-outreach/page";
import SupplementDataPage from "./app/administration/supplement-data/page";
import CreateCandidatePage from "./app/testing/candidate-mgmt/create/page";
import EditCandidatePage from "./app/testing/candidate-mgmt/edit/[id]/page";
import CandidateMgmtPage from "./app/testing/candidate-mgmt/page";
import CreateJobOrderPage from "./app/testing/job-order-mgmt/create/page";
import EditJobOrderPage from "./app/testing/job-order-mgmt/edit/[id]/page";
import JobOrderMgmtPage from "./app/testing/job-order-mgmt/page";
import JobOrderRecipientsPage from "./app/testing/job-order-mgmt/recipients/[id]/page";
import ViewSQSPage from "./app/testing/view-sqs/page";
import WebchatPage from "./app/features/webchat/page";
import SenderPersonaPage from "./app/administration/sender-persona/page";
import SenderPersonaFormPage from "./app/administration/sender-persona/form/page";
import SenderPersonaFormEditPage from "./app/administration/sender-persona/form/[id]/page";
import SenderPersonaDetailsPage from "./app/administration/sender-persona/details/[id]/page";
import TransactionTrackingPage from "./app/analytics/transaction-tracking/page";
import FailureTransactionsPage from "./app/analytics/failure-transactions/page";
import KnowledgeBaseSetupPage from "./app/administration/knowledge-base-setup/page";
import FileManagementPage from "./app/features/file-management/page";
import CommunicationIntegrationPage from "./app/administration/communication-integration/page";
import ComplianceQuietHoursPage from "./app/administration/compliance-quiet-hours/page";
import ConstantsManagementPage from "./app/administration/constants-management/page";
import RoleManagementPage from "./app/administration/role-management/page";
import { ROUTES } from "./constants/routes";
import AuthLayout from "./layouts/auth-layout";
import MainLayout from "./layouts/main-layout";
import { PageHeaderProvider } from "./contexts/page-header-context";

function App() {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  }));

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-right" />
      <BrowserRouter>
        <PageHeaderProvider>
          <Routes>
            <Route element={<AuthLayout />}>
              <Route element={<MainLayout />}>
                {/* Home/Landing Page */}
                <Route path={ROUTES.HOME} element={<HomePage />} />

                {/* Feature Pages */}
                <Route path={ROUTES.SUPPLEMENT_DATA} element={<SupplementDataPage />} />
                <Route path={ROUTES.WEBCHAT} element={<WebchatPage />} />

                {/* Campaign Management Routes */}
                <Route path={ROUTES.CAMPAIGN_MANAGEMENT} element={<CampaignManagementPage />} />
                <Route path={ROUTES.CAMPAIGN_CREATE} element={<CreateCampaignPage />} />
                <Route path={ROUTES.CAMPAIGN_VIEW} element={<ViewCampaignPage />} />
                <Route path={ROUTES.CAMPAIGN_TPMO} element={<TpmoPage />} />

                {/* Candidate Outreach Routes */}
                <Route path={ROUTES.PRESCREENING} element={<PrescreeningPage />} />
                <Route path={ROUTES.PRESCREENING_EDIT} element={<PrescreeningEditPage />} />
                <Route path={ROUTES.PRESCREENING_DETAILS} element={<PrescreeningDetailsPage />} />

                {/* Testing Routes */}
                <Route path={ROUTES.TESTING_CANDIDATE_MGMT} element={<CandidateMgmtPage />} />
                <Route path={ROUTES.TESTING_CANDIDATE_MGMT_CREATE} element={<CreateCandidatePage />} />
                <Route path={ROUTES.TESTING_CANDIDATE_MGMT_EDIT} element={<EditCandidatePage />} />
                <Route path={ROUTES.TESTING_JOB_ORDER_MGMT} element={<JobOrderMgmtPage />} />
                <Route path={ROUTES.TESTING_JOB_ORDER_CREATE} element={<CreateJobOrderPage />} />
                <Route path={ROUTES.TESTING_JOB_ORDER_EDIT} element={<EditJobOrderPage />} />
                <Route path={ROUTES.TESTING_JOB_ORDER_RECIPIENTS} element={<JobOrderRecipientsPage />} />
                <Route path={ROUTES.TESTING_VIEW_SQS} element={<ViewSQSPage />} />

                {/* Sender Persona Routes */}
                <Route path={ROUTES.SENDER_PERSONA} element={<SenderPersonaPage />} />
                <Route path={ROUTES.SENDER_PERSONA_FORM} element={<SenderPersonaFormPage />} />
                <Route path={ROUTES.SENDER_PERSONA_FORM_EDIT} element={<SenderPersonaFormEditPage />} />
                <Route path={ROUTES.SENDER_PERSONA_DETAILS} element={<SenderPersonaDetailsPage />} />

                {/* Analytics Routes */}
                <Route path={ROUTES.ANALYTICS_TRANSACTION_TRACKING} element={<TransactionTrackingPage />} />
                <Route path={ROUTES.ANALYTICS_FAILURE_TRANSACTIONS} element={<FailureTransactionsPage />} />

                {/* Knowledge Base Setup Routes */}
                <Route path={ROUTES.KNOWLEDGE_BASE_SETUP} element={<KnowledgeBaseSetupPage />} />

                {/* File Management Routes */}
                <Route path={ROUTES.FILE_MANAGEMENT} element={<FileManagementPage />} />

                {/* Communication Integration Routes */}
                <Route path={ROUTES.COMMUNICATION_INTEGRATION} element={<CommunicationIntegrationPage />} />

                {/* Compliance & Quiet Hours Routes */}
                <Route path={ROUTES.COMPLIANCE_QUIET_HOURS} element={<ComplianceQuietHoursPage />} />

                {/* Constants Management Routes */}
                <Route path={ROUTES.CONSTANTS_MANAGEMENT} element={<ConstantsManagementPage />} />

                {/* Role Management Routes */}
                <Route path={ROUTES.ROLE_MANAGEMENT} element={<RoleManagementPage />} />

                {/* 404 Page */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
        </PageHeaderProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
